// frontend/src/actions/paymentActions.js

import * as actionTypes from "../constants/paymentConstants";
import { PAYMENT_PROVIDERS } from "../constants/paymentConstants";
import api from "../utils/axiosInstance";
import {
  USER_FACING_FALLBACK,
  getApiErrorMessage,
} from "../utils/errorMessages";

const PAYMENTS_BASE_URL = "/payments";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

function normalizeUpper(value = "") {
  return safeText(value).toUpperCase();
}

function encodePath(value = "") {
  return encodeURIComponent(safeText(value));
}

function unique(values = []) {
  return [...new Set(values.filter(Boolean))];
}

function buildIdempotencyKey(prefix = "req") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getHeader(error = {}, name = "") {
  const headers = error?.response?.headers || {};
  const target = normalizeLower(name);

  const key = Object.keys(headers || {}).find(
    (headerName) => normalizeLower(headerName) === target,
  );

  return key ? headers[key] : "";
}

function containsUnsafeCustomerStockCopy(value = "") {
  const text = normalizeLower(value);

  if (!text) return false;

  return (
    text.includes("reserved") ||
    text.includes("temporarily reserved") ||
    text.includes("allocated") ||
    text.includes("held") ||
    text.includes("held for")
  );
}

function containsSensitiveCustomerCopy(value = "") {
  const text = normalizeLower(value);

  return (
    text.includes("secret") ||
    text.includes("token") ||
    text.includes("authorization") ||
    text.includes("cookie") ||
    text.includes("jwt") ||
    text.includes("mongo") ||
    text.includes("api key") ||
    text.includes("client_secret") ||
    text.includes("access_token") ||
    text.includes("refresh_token")
  );
}

function looksLikeRawProviderCode(value = "") {
  const text = safeText(value);

  return Boolean(text && /^[A-Z0-9_.:-]{8,}$/.test(text));
}

function customerSafeMessage(value = "", fallback = USER_FACING_FALLBACK) {
  const text = safeText(value);

  if (!text) return fallback;
  if (containsSensitiveCustomerCopy(text)) return fallback;
  if (looksLikeRawProviderCode(text)) return fallback;

  return containsUnsafeCustomerStockCopy(text) ? fallback : text;
}

function normalizeApiError(error, fallback = USER_FACING_FALLBACK) {
  const data = error?.response?.data || error?.payload || {};
  const fallbackMessage = getApiErrorMessage(error, { fallback });
  const status = Number(
    error?.response?.status || error?.statusCode || error?.status || 0,
  );
  const requestId = safeText(
    data?.requestId || error?.requestId || getHeader(error, "x-request-id"),
  );
  const retryAfterSeconds = Number(
    data?.retryAfterSeconds ||
      error?.retryAfterSeconds ||
      getHeader(error, "retry-after") ||
      0,
  );
  const code = safeText(data?.code || error?.errorCode || error?.code);

  return {
    message: customerSafeMessage(
      data?.customerMessage ||
        data?.message ||
        error?.normalizedMessage,
      fallbackMessage,
    ),
    code,
    debugCode: safeText(data?.debugCode || error?.errorCode || code),
    status,
    requestId,
    retryAfterSeconds,
    action: safeText(data?.action),
    payload: {
      code,
      status,
      requestId,
      retryAfterSeconds,
      action: safeText(data?.action),
    },
  };
}

function toBoolean(value, fallback = false) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  const normalized = normalizeLower(value);

  if (["true", "1", "yes", "enabled", "on"].includes(normalized)) return true;
  if (["false", "0", "no", "disabled", "off"].includes(normalized)) {
    return false;
  }

  return fallback;
}

function normalizeProvider(provider = "") {
  const normalized = normalizeLower(provider);

  if (
    normalized === PAYMENT_PROVIDERS.STRIPE ||
    normalized === PAYMENT_PROVIDERS.PAYPAL ||
    normalized === PAYMENT_PROVIDERS.SUMUP
  ) {
    return normalized;
  }

  return "";
}

function assertOrderNumber(orderNumber = "") {
  const safeOrderNumber = normalizeUpper(orderNumber);

  if (!safeOrderNumber) {
    throw Object.assign(new Error("Order number is required."), {
      message: "Order number is required.",
      code: "ORDER_NUMBER_REQUIRED",
      status: 400,
      payload: null,
    });
  }

  return safeOrderNumber;
}

function assertPaymentProvider(provider = "") {
  const safeProvider = normalizeProvider(provider);

  if (!safeProvider) {
    throw Object.assign(
      new Error('Payment provider must be "stripe", "paypal", or "sumup".'),
      {
        message: 'Payment provider must be "stripe", "paypal", or "sumup".',
        code: "PAYMENT_PROVIDER_REQUIRED",
        status: 400,
        payload: null,
      },
    );
  }

  return safeProvider;
}

function getProviderEnabledFromLegacyShape(data = {}, provider = "") {
  if (provider === PAYMENT_PROVIDERS.STRIPE) {
    return toBoolean(
      data?.stripeEnabled ??
        data?.stripe?.enabled ??
        data?.providers?.stripe?.enabled,
      false,
    );
  }

  if (provider === PAYMENT_PROVIDERS.PAYPAL) {
    return toBoolean(
      data?.paypalEnabled ??
        data?.paypal?.enabled ??
        data?.providers?.paypal?.enabled,
      false,
    );
  }

  if (provider === PAYMENT_PROVIDERS.SUMUP) {
    return toBoolean(
      data?.sumupEnabled ??
        data?.sumup?.enabled ??
        data?.providers?.sumup?.enabled,
      false,
    );
  }

  return false;
}

function getEnabledProviders(data = {}) {
  const explicitProviders = Array.isArray(data?.enabledProviders)
    ? data.enabledProviders.map(normalizeProvider).filter(Boolean)
    : [];

  if (explicitProviders.length) {
    return unique(explicitProviders);
  }

  const providerMap =
    data?.providers && typeof data.providers === "object" ? data.providers : {};

  const mappedProviders = Object.keys(providerMap)
    .map(normalizeProvider)
    .filter(
      (provider) => provider && toBoolean(providerMap?.[provider]?.enabled),
    );

  if (mappedProviders.length) {
    return unique(mappedProviders);
  }

  return unique(
    [
      PAYMENT_PROVIDERS.STRIPE,
      PAYMENT_PROVIDERS.PAYPAL,
      PAYMENT_PROVIDERS.SUMUP,
    ].filter((provider) => getProviderEnabledFromLegacyShape(data, provider)),
  );
}

function normalizePaymentConfig(data = {}) {
  const enabledProviders = getEnabledProviders(data);

  const stripeEnabled = enabledProviders.includes(PAYMENT_PROVIDERS.STRIPE);
  const paypalEnabled = enabledProviders.includes(PAYMENT_PROVIDERS.PAYPAL);
  const sumupEnabled = enabledProviders.includes(PAYMENT_PROVIDERS.SUMUP);

  const stripePublicKey = safeText(
    data?.providers?.stripe?.publishableKey ||
      data?.providers?.stripe?.publicKey ||
      data?.stripe?.publishableKey ||
      data?.stripe?.publicKey ||
      data?.publishableKey ||
      data?.publicKey ||
      data?.stripePublishableKey ||
      data?.stripePublicKey,
  );

  const paypalClientId = safeText(
    data?.providers?.paypal?.clientId ||
      data?.paypal?.clientId ||
      data?.paypalClientId,
  );

  const stripeConfig = {
    ...(data?.providers?.stripe || {}),
    ...(data?.stripe || {}),
    enabled: stripeEnabled,
    provider: PAYMENT_PROVIDERS.STRIPE,
    publicKey: stripePublicKey,
    publishableKey: stripePublicKey,
  };

  const paypalConfig = {
    ...(data?.providers?.paypal || {}),
    ...(data?.paypal || {}),
    enabled: paypalEnabled,
    provider: PAYMENT_PROVIDERS.PAYPAL,
    clientId: paypalClientId,
  };

  const sumupConfig = {
    ...(data?.providers?.sumup || {}),
    ...(data?.sumup || {}),
    enabled: sumupEnabled,
    provider: PAYMENT_PROVIDERS.SUMUP,
  };

  return {
    ...data,
    loaded: true,
    enabledProviders,
    stripeEnabled,
    paypalEnabled,
    sumupEnabled,
    stripePublicKey,
    stripePublishableKey: stripePublicKey,
    paypalClientId,
    providers: {
      ...(data?.providers || {}),
      stripe: stripeConfig,
      paypal: paypalConfig,
      sumup: sumupConfig,
    },
    stripe: stripeConfig,
    paypal: paypalConfig,
    sumup: sumupConfig,
  };
}

function getCustomerCheckout(data = {}) {
  return data?.customerCheckout || data?.order?.customerCheckout || null;
}

function getPaymentWindowExpiresAt(data = {}) {
  return (
    safeText(data?.paymentWindowExpiresAt) ||
    safeText(data?.customerCheckout?.paymentWindowExpiresAt) ||
    safeText(data?.order?.paymentWindowExpiresAt) ||
    safeText(data?.order?.customerCheckout?.paymentWindowExpiresAt) ||
    ""
  );
}

function sanitizePayment(payment = null) {
  if (!payment || typeof payment !== "object") return payment || null;

  return {
    ...payment,
    clientSecret: "",
    failureCode:
      payment.failureCode || payment.failureMessage ? "PAYMENT_FAILED" : "",
    failureMessage:
      payment.failureCode || payment.failureMessage
        ? "Payment could not be completed."
        : "",
  };
}

function sanitizeOrder(order = null) {
  if (!order || typeof order !== "object") return order || null;

  return {
    ...order,
    payment: sanitizePayment(order.payment),
  };
}

function normalizePaymentPayload(data = {}) {
  const order = sanitizeOrder(data?.order || null);
  const payment = sanitizePayment(data?.payment || order?.payment || null);

  const approvalUrl = safeText(
    data?.checkoutUrl ||
      data?.approvalUrl ||
      data?.approveUrl ||
      data?.payerActionUrl ||
      payment?.checkoutUrl ||
      payment?.approvalUrl ||
      payment?.approveUrl ||
      payment?.payerActionUrl,
  );

  const approveUrl = safeText(
    data?.approveUrl ||
      data?.approvalUrl ||
      payment?.approveUrl ||
      payment?.approvalUrl,
  );

  const payerActionUrl = safeText(
    data?.payerActionUrl || payment?.payerActionUrl || approvalUrl,
  );

  return {
    ...data,
    order,
    payment,
    orderNumber: safeText(
      data?.orderNumber || order?.orderNumber || payment?.orderNumber,
    ),
    clientSecret: "",
    checkoutUrl: approvalUrl,
    approvalUrl,
    approveUrl,
    payerActionUrl,
    nextAction: data?.nextAction || payment?.nextAction || null,
    customerCheckout: getCustomerCheckout(data),
    paymentWindowExpiresAt: getPaymentWindowExpiresAt(data),
  };
}

function getPaymentOrderRoute(orderNumber = "") {
  return `${PAYMENTS_BASE_URL}/order/${encodePath(assertOrderNumber(orderNumber))}`;
}

function getPaymentCancelRoute(orderNumber = "") {
  return `${getPaymentOrderRoute(orderNumber)}/cancel`;
}

function getPaymentConfirmRoute(orderNumber = "") {
  return `${getPaymentOrderRoute(orderNumber)}/confirm`;
}

/* -------------------------------------------------------------------------- */
/*                                  Config                                    */
/* -------------------------------------------------------------------------- */

export const getPaymentConfig = () => async (dispatch) => {
  dispatch({ type: actionTypes.PAYMENT_CONFIG_REQUEST });

  try {
    const { data } = await api.get(`${PAYMENTS_BASE_URL}/config`);
    const normalized = normalizePaymentConfig(data);

    dispatch({
      type: actionTypes.PAYMENT_CONFIG_SUCCESS,
      payload: normalized,
    });

    return normalized;
  } catch (error) {
    const payloadError = normalizeApiError(
      error,
      "Could not load payment configuration.",
    );

    dispatch({
      type: actionTypes.PAYMENT_CONFIG_FAIL,
      payload: payloadError,
    });

    throw payloadError;
  }
};

/* -------------------------------------------------------------------------- */
/*                              Existing payment                              */
/* -------------------------------------------------------------------------- */

export const getPaymentForExistingOrder =
  ({ orderNumber } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.PAYMENT_ORDER_GET_REQUEST });

    try {
      const { data } = await api.get(getPaymentOrderRoute(orderNumber));
      const normalized = normalizePaymentPayload(data);

      dispatch({
        type: actionTypes.PAYMENT_ORDER_GET_SUCCESS,
        payload: normalized,
      });

      dispatch({
        type: actionTypes.PAYMENT_SET_CURRENT,
        payload: normalized,
      });

      return normalized;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not load payment details.",
      );

      dispatch({
        type: actionTypes.PAYMENT_ORDER_GET_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const createPaymentForExistingOrder =
  ({
    orderNumber,
    provider = "",
    paymentProvider = "",
    paymentMethod = "",
    idempotencyKey = "",
  } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.PAYMENT_ORDER_CREATE_REQUEST });

    try {
      const safeProvider = assertPaymentProvider(
        provider || paymentProvider || paymentMethod,
      );

      const safeIdempotencyKey =
        safeText(idempotencyKey) || buildIdempotencyKey("payment");

      const payload = {
        provider: safeProvider,
        paymentProvider: safeProvider,
        paymentMethod: safeProvider,
      };

      const { data } = await api.post(
        getPaymentOrderRoute(orderNumber),
        payload,
        {
          headers: {
            "Idempotency-Key": safeIdempotencyKey,
          },
        },
      );

      const normalized = normalizePaymentPayload({
        ...data,
        idempotencyKey: safeIdempotencyKey,
      });

      dispatch({
        type: actionTypes.PAYMENT_ORDER_CREATE_SUCCESS,
        payload: normalized,
      });

      dispatch({
        type: actionTypes.PAYMENT_SET_CURRENT,
        payload: normalized,
      });

      return normalized;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not start payment. Please try again.",
      );

      dispatch({
        type: actionTypes.PAYMENT_ORDER_CREATE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const cancelPendingOrderPayment =
  ({ orderNumber, reason = "", idempotencyKey = "" } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.PAYMENT_ORDER_CANCEL_REQUEST });

    try {
      const safeIdempotencyKey =
        safeText(idempotencyKey) || buildIdempotencyKey("payment-cancel");

      const { data } = await api.post(
        getPaymentCancelRoute(orderNumber),
        {
          reason: safeText(reason),
        },
        {
          headers: {
            "Idempotency-Key": safeIdempotencyKey,
          },
        },
      );

      const normalized = normalizePaymentPayload({
        ...data,
        idempotencyKey: safeIdempotencyKey,
      });

      dispatch({
        type: actionTypes.PAYMENT_ORDER_CANCEL_SUCCESS,
        payload: normalized,
      });

      dispatch({
        type: actionTypes.PAYMENT_SET_CURRENT,
        payload: normalized,
      });

      return normalized;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not cancel this payment.",
      );

      dispatch({
        type: actionTypes.PAYMENT_ORDER_CANCEL_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const confirmPaymentForExistingOrder =
  ({
    orderNumber,
    provider = "",
    paymentProvider = "",
    paymentMethod = "",
    idempotencyKey = "",
  } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.PAYMENT_ORDER_CONFIRM_REQUEST });

    try {
      const safeProvider = assertPaymentProvider(
        provider || paymentProvider || paymentMethod,
      );

      const safeIdempotencyKey =
        safeText(idempotencyKey) || buildIdempotencyKey("payment-confirm");

      const { data } = await api.post(
        getPaymentConfirmRoute(orderNumber),
        {
          provider: safeProvider,
          paymentProvider: safeProvider,
          paymentMethod: safeProvider,
        },
        {
          headers: {
            "Idempotency-Key": safeIdempotencyKey,
          },
        },
      );

      const normalized = normalizePaymentPayload({
        ...data,
        idempotencyKey: safeIdempotencyKey,
      });

      dispatch({
        type: actionTypes.PAYMENT_ORDER_CONFIRM_SUCCESS,
        payload: normalized,
      });

      dispatch({
        type: actionTypes.PAYMENT_SET_CURRENT,
        payload: normalized,
      });

      return normalized;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Payment could not be confirmed. Please refresh your order.",
      );

      dispatch({
        type: actionTypes.PAYMENT_ORDER_CONFIRM_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

/* -------------------------------------------------------------------------- */
/*                         Backwards-compatible aliases                        */
/* -------------------------------------------------------------------------- */

export const getOrderPayment = getPaymentForExistingOrder;
export const createOrderPayment = createPaymentForExistingOrder;
export const cancelPaymentForExistingOrder = cancelPendingOrderPayment;
export const confirmOrderPayment = confirmPaymentForExistingOrder;

/* -------------------------------------------------------------------------- */
/*                                   State                                    */
/* -------------------------------------------------------------------------- */

export const setCurrentPayment = (payload) => ({
  type: actionTypes.PAYMENT_SET_CURRENT,
  payload,
});

export const clearCurrentPayment = () => ({
  type: actionTypes.PAYMENT_CLEAR_CURRENT,
});

export const resetPaymentConfig = () => ({
  type: actionTypes.PAYMENT_CONFIG_RESET,
});

export const resetGetOrderPayment = () => ({
  type: actionTypes.PAYMENT_ORDER_GET_RESET,
});

export const resetCreateOrderPayment = () => ({
  type: actionTypes.PAYMENT_ORDER_CREATE_RESET,
});

export const resetCancelPendingPayment = () => ({
  type: actionTypes.PAYMENT_ORDER_CANCEL_RESET,
});

export const resetConfirmPayment = () => ({
  type: actionTypes.PAYMENT_ORDER_CONFIRM_RESET,
});

export const resetPaymentError = () => ({
  type: actionTypes.PAYMENT_RESET_ERROR,
});

export const resetPaymentMeta = () => ({
  type: actionTypes.PAYMENT_RESET_META,
});

export const resetPaymentState = () => ({
  type: actionTypes.PAYMENT_RESET_STATE,
});
